import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    HStack,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    SimpleGrid,
    Stack,
    Stat,
    StatLabel,
    StatNumber,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {MdAdd, MdAutoAwesome, MdDelete, MdPrint, MdShuffle, MdTune} from "react-icons/md";
import {
    SLOT_TYPES,
    generateTimetable,
    minutesToDisplay,
} from "../../../utils/TimetableGenerator";

const STORAGE_KEY = "smartTimetable";

const DEFAULT_CONFIG = {
    startHour: "10:00",
    endHour: "16:00",
    periodDuration: 45,
    miniBreak: 10,
    mainBreak: 40,
    subjects: ["Nepali", "English", "Mathematics", "Science", "Social", "Computer"],
};

const readStored = () => {
    try {
        const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return data && typeof data === "object" ? data : null;
    } catch (e) {
        return null;
    }
};

const writeStored = (payload) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

const SLOT_STYLES = {
    [SLOT_TYPES.PERIOD]: {label: "Period", scheme: "blue", bg: "white"},
    [SLOT_TYPES.MINI_BREAK]: {label: "Mini Break", scheme: "orange", bg: "orange.50"},
    [SLOT_TYPES.MAIN_BREAK]: {label: "Main Break", scheme: "purple", bg: "purple.50"},
};

/* ---------------------- constraints modal ---------------------- */

function ConstraintsModal({isOpen, onClose, initialConfig, onGenerate}) {
    const toast = useToast();
    const [config, setConfig] = useState(initialConfig);

    useEffect(() => {
        if (isOpen) {
            setConfig(initialConfig);
        }
    }, [isOpen, initialConfig]);

    const update = (patch) => setConfig((prev) => ({...prev, ...patch}));

    const setSubjectCount = (count) => {
        const next = [...config.subjects];
        const target = Math.max(1, count);
        if (target > next.length) {
            while (next.length < target) {
                next.push("");
            }
        } else {
            next.length = target;
        }
        update({subjects: next});
    };

    const setSubjectName = (index, value) => {
        const next = [...config.subjects];
        next[index] = value;
        update({subjects: next});
    };

    const removeSubject = (index) => {
        if (config.subjects.length <= 1) {
            return;
        }
        update({subjects: config.subjects.filter((_, i) => i !== index)});
    };

    const handleGenerate = () => {
        const cleaned = {
            ...config,
            subjects: config.subjects.map((name) => name.trim()).filter(Boolean),
        };
        if (cleaned.subjects.length === 0) {
            toast({title: "Add at least one subject", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        onGenerate(cleaned);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Generate Timetable</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Stack spacing={5}>
                        <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                            <FormControl>
                                <FormLabel>Start hour</FormLabel>
                                <Input type="time" value={config.startHour} onChange={(e) => update({startHour: e.target.value})}/>
                            </FormControl>
                            <FormControl>
                                <FormLabel>End hour</FormLabel>
                                <Input type="time" value={config.endHour} onChange={(e) => update({endHour: e.target.value})}/>
                            </FormControl>
                        </SimpleGrid>

                        <SimpleGrid columns={{base: 1, md: 3}} spacing={4}>
                            <FormControl>
                                <FormLabel>Period duration (min)</FormLabel>
                                <NumberInput min={5} max={180} value={config.periodDuration} onChange={(_, v) => update({periodDuration: Number.isNaN(v) ? 0 : v})}>
                                    <NumberInputField/>
                                    <NumberInputStepper>
                                        <NumberIncrementStepper/>
                                        <NumberDecrementStepper/>
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Mini break (min)</FormLabel>
                                <NumberInput min={0} max={60} value={config.miniBreak} onChange={(_, v) => update({miniBreak: Number.isNaN(v) ? 0 : v})}>
                                    <NumberInputField/>
                                    <NumberInputStepper>
                                        <NumberIncrementStepper/>
                                        <NumberDecrementStepper/>
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Main break (min)</FormLabel>
                                <NumberInput min={0} max={120} value={config.mainBreak} onChange={(_, v) => update({mainBreak: Number.isNaN(v) ? 0 : v})}>
                                    <NumberInputField/>
                                    <NumberInputStepper>
                                        <NumberIncrementStepper/>
                                        <NumberDecrementStepper/>
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                        </SimpleGrid>

                        <Divider/>

                        <Box>
                            <Flex justify="space-between" align="center" mb={3}>
                                <FormLabel mb={0}>Subjects</FormLabel>
                                <HStack>
                                    <Text fontSize="sm" color="gray.500">Count</Text>
                                    <NumberInput
                                        size="sm"
                                        maxW="90px"
                                        min={1}
                                        max={20}
                                        value={config.subjects.length}
                                        onChange={(_, v) => setSubjectCount(Number.isNaN(v) ? 1 : v)}
                                    >
                                        <NumberInputField/>
                                        <NumberInputStepper>
                                            <NumberIncrementStepper/>
                                            <NumberDecrementStepper/>
                                        </NumberInputStepper>
                                    </NumberInput>
                                </HStack>
                            </Flex>
                            <Stack spacing={2}>
                                {config.subjects.map((name, index) => (
                                    <HStack key={index}>
                                        <Text fontSize="sm" color="gray.400" w="24px">{index + 1}.</Text>
                                        <Input
                                            value={name}
                                            placeholder={`Subject ${index + 1} name`}
                                            onChange={(e) => setSubjectName(index, e.target.value)}
                                        />
                                        <IconButton
                                            aria-label="Remove subject"
                                            icon={<MdDelete/>}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="red"
                                            onClick={() => removeSubject(index)}
                                            isDisabled={config.subjects.length <= 1}
                                        />
                                    </HStack>
                                ))}
                            </Stack>
                            <Button
                                mt={3}
                                size="sm"
                                leftIcon={<MdAdd/>}
                                variant="outline"
                                onClick={() => setSubjectCount(config.subjects.length + 1)}
                            >
                                Add subject
                            </Button>
                        </Box>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="teal" leftIcon={<MdAutoAwesome/>} onClick={handleGenerate}>
                        Generate
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/* ----------------------------- page ----------------------------- */

function SmartTimetable() {
    const {isOpen, onOpen, onClose} = useDisclosure();
    const toast = useToast();
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [result, setResult] = useState(null);

    useEffect(() => {
        const stored = readStored();
        if (stored) {
            setConfig(stored.config || DEFAULT_CONFIG);
            setResult(stored.result || null);
        }
    }, []);

    const handleGenerate = useCallback(
        (cleanedConfig) => {
            const generated = generateTimetable(cleanedConfig);
            setConfig(cleanedConfig);
            setResult(generated);
            writeStored({config: cleanedConfig, result: generated, savedAt: Date.now()});

            const errors = generated.conflicts.filter((c) => c.level === "error").length;
            toast({
                title: errors ? "Generated with conflicts" : "Timetable generated",
                description: errors ? `${errors} conflict(s) need attention.` : "Saved to this device.",
                status: errors ? "warning" : "success",
                duration: 3000,
                isClosable: true,
            });
        },
        [toast]
    );

    const clearAll = () => {
        localStorage.removeItem(STORAGE_KEY);
        setResult(null);
        toast({title: "Timetable cleared", status: "info", duration: 2000, isClosable: true});
    };

    const shufflePeriods = () => {
        if (!result) {
            return;
        }
        const periodLabels = result.slots
            .filter((slot) => slot.type === SLOT_TYPES.PERIOD)
            .map((slot) => slot.label);

        if (periodLabels.length < 2) {
            toast({title: "Need at least 2 periods to shuffle", status: "info", duration: 2000, isClosable: true});
            return;
        }

        // Fisher–Yates shuffle of subject assignments (times & breaks stay fixed).
        for (let i = periodLabels.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [periodLabels[i], periodLabels[j]] = [periodLabels[j], periodLabels[i]];
        }

        let pointer = 0;
        const newSlots = result.slots.map((slot) =>
            slot.type === SLOT_TYPES.PERIOD ? {...slot, label: periodLabels[pointer++]} : slot
        );
        const newResult = {...result, slots: newSlots};
        setResult(newResult);
        writeStored({config, result: newResult, savedAt: Date.now()});

        toast({
            title: "Periods shuffled",
            description: "Subjects were reordered across the periods.",
            status: "success",
            duration: 2000,
            isClosable: true,
        });
    };

    const printTimetable = () => {
        if (!result) {
            return;
        }
        const userInfo = (() => {
            try {
                return JSON.parse(localStorage.getItem("userInfo")) || {};
            } catch (e) {
                return {};
            }
        })();
        const schoolName = userInfo?.clientName || "School Bell Timetable";

        const rows = result.slots
            .map((slot, index) => {
                const style = SLOT_STYLES[slot.type];
                return `<tr class="${slot.type}">
                    <td>${index + 1}</td>
                    <td>${style.label}</td>
                    <td>${slot.label}</td>
                    <td>${minutesToDisplay(slot.start)}</td>
                    <td>${minutesToDisplay(slot.end)}</td>
                    <td style="text-align:right">${slot.end - slot.start} min</td>
                </tr>`;
            })
            .join("");

        const printWindow = window.open("", "_blank", "width=900,height=700");
        if (!printWindow) {
            toast({
                title: "Pop-up blocked",
                description: "Allow pop-ups to print the timetable.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        printWindow.document.write(`<!doctype html><html><head><title>${schoolName} — Timetable</title>
            <meta charset="utf-8"/>
            <style>
                * { font-family: Arial, Helvetica, sans-serif; }
                body { padding: 24px; color: #1a202c; }
                h1 { margin: 0 0 4px; font-size: 20px; }
                p.sub { margin: 0 0 16px; color: #718096; font-size: 12px; }
                table { width: 100%; border-collapse: collapse; }
                th, td { padding: 8px 10px; border: 1px solid #e2e8f0; font-size: 13px; text-align: left; }
                thead th { background: #319795; color: #fff; }
                tr.MINI_BREAK td { background: #fffaf0; }
                tr.MAIN_BREAK td { background: #faf5ff; }
                @media print { body { padding: 0; } }
            </style></head><body>
            <h1>${schoolName}</h1>
            <p class="sub">Bell timetable · generated ${new Date().toLocaleString()}</p>
            <table>
                <thead><tr><th>#</th><th>Type</th><th>Subject / Activity</th><th>Start</th><th>End</th><th style="text-align:right">Duration</th></tr></thead>
                <tbody>${rows}</tbody>
            </table>
            </body></html>`);
        printWindow.document.close();
        printWindow.focus();
        printWindow.onload = () => {
            printWindow.print();
        };
        // Fallback in case onload already fired.
        setTimeout(() => printWindow.print(), 400);

        toast({title: "Opening print preview", status: "info", duration: 1500, isClosable: true});
    };

    const conflicts = result?.conflicts || [];
    const slots = result?.slots || [];
    const summary = result?.summary;

    const sortedConflicts = useMemo(() => {
        const order = {error: 0, warning: 1, info: 2};
        return conflicts.slice().sort((a, b) => order[a.level] - order[b.level]);
    }, [conflicts]);

    return (
        <Flex direction="column" pt={{base: "110px", md: "80px"}} px={{base: "4", md: "8"}} pb="10" bg="gray.50" minH="100vh">
            <Flex justify="space-between" align={{base: "flex-start", md: "center"}} direction={{base: "column", md: "row"}} gap={4} mb={6}>
                <Box>
                    <Text fontSize="2xl" fontWeight="bold">Smart Timetable</Text>
                    <Text color="gray.600" mt={1}>
                        Feed your constraints and let the generator build the full bell schedule and flag conflicts.
                    </Text>
                </Box>
                <HStack>
                    {result && (
                        <Button variant="outline" leftIcon={<MdTune/>} onClick={onOpen}>Edit constraints</Button>
                    )}
                    <Button colorScheme="teal" leftIcon={<MdAutoAwesome/>} onClick={onOpen}>
                        Generate Timetable
                    </Button>
                </HStack>
            </Flex>

            {!result ? (
                <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    bg="white"
                    borderRadius="16px"
                    boxShadow="sm"
                    py="80px"
                    px="6"
                    textAlign="center"
                >
                    <Box color="teal.400" fontSize="56px" mb={4}><MdAutoAwesome/></Box>
                    <Text fontSize="lg" fontWeight="bold">No timetable yet</Text>
                    <Text color="gray.500" maxW="420px" mt={2}>
                        Click “Generate Timetable”, enter the number of subjects, period duration, break durations and
                        school hours — the schedule appears here.
                    </Text>
                </Flex>
            ) : (
                <Stack spacing={6}>
                    {summary && (
                        <SimpleGrid columns={{base: 2, md: 4}} spacing={4}>
                            <Stat bg="white" p={4} borderRadius="12px" boxShadow="sm">
                                <StatLabel color="gray.500">Periods</StatLabel>
                                <StatNumber>{summary.periodCount}</StatNumber>
                            </Stat>
                            <Stat bg="white" p={4} borderRadius="12px" boxShadow="sm">
                                <StatLabel color="gray.500">Breaks</StatLabel>
                                <StatNumber>{summary.breakCount}</StatNumber>
                            </Stat>
                            <Stat bg="white" p={4} borderRadius="12px" boxShadow="sm">
                                <StatLabel color="gray.500">School hours</StatLabel>
                                <StatNumber>{Math.floor(summary.totalMinutes / 60)}h {summary.totalMinutes % 60}m</StatNumber>
                            </Stat>
                            <Stat bg="white" p={4} borderRadius="12px" boxShadow="sm">
                                <StatLabel color="gray.500">Closing bell</StatLabel>
                                <StatNumber fontSize="lg">{minutesToDisplay(summary.endMinutes)}</StatNumber>
                            </Stat>
                        </SimpleGrid>
                    )}

                    {sortedConflicts.length > 0 && (
                        <Stack spacing={2}>
                            {sortedConflicts.map((conflict, index) => (
                                <Alert
                                    key={index}
                                    status={conflict.level === "error" ? "error" : conflict.level === "warning" ? "warning" : "info"}
                                    borderRadius="10px"
                                >
                                    <AlertIcon/>
                                    {conflict.message}
                                </Alert>
                            ))}
                        </Stack>
                    )}

                    <Box bg="white" borderRadius="16px" boxShadow="sm" overflowX="auto">
                        <Flex justify="space-between" align="center" p={4} wrap="wrap" gap={2}>
                            <Text fontWeight="bold">Generated schedule</Text>
                            <HStack spacing={2} wrap="wrap">
                                <Button size="sm" variant="outline" colorScheme="teal" leftIcon={<MdShuffle/>} onClick={shufflePeriods}>
                                    Shuffle
                                </Button>
                                <Button size="sm" variant="outline" colorScheme="blue" leftIcon={<MdPrint/>} onClick={printTimetable}>
                                    Print
                                </Button>
                                <Button size="sm" variant="ghost" colorScheme="red" leftIcon={<MdDelete/>} onClick={clearAll}>
                                    Clear
                                </Button>
                            </HStack>
                        </Flex>
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>#</Th>
                                    <Th>Type</Th>
                                    <Th>Subject / Activity</Th>
                                    <Th>Start</Th>
                                    <Th>End</Th>
                                    <Th isNumeric>Duration</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {slots.map((slot, index) => {
                                    const style = SLOT_STYLES[slot.type];
                                    return (
                                        <Tr key={index} bg={style.bg}>
                                            <Td>{index + 1}</Td>
                                            <Td>
                                                <Badge colorScheme={style.scheme} borderRadius="full" px={2}>
                                                    {style.label}
                                                </Badge>
                                            </Td>
                                            <Td fontWeight={slot.type === SLOT_TYPES.PERIOD ? "semibold" : "normal"}>
                                                {slot.label}
                                            </Td>
                                            <Td>{minutesToDisplay(slot.start)}</Td>
                                            <Td>{minutesToDisplay(slot.end)}</Td>
                                            <Td isNumeric>{slot.end - slot.start} min</Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                </Stack>
            )}

            <ConstraintsModal isOpen={isOpen} onClose={onClose} initialConfig={config} onGenerate={handleGenerate}/>
        </Flex>
    );
}

export default SmartTimetable;
